import { computed, DestroyRef, Directive, effect, ElementRef, inject, input, Renderer2 } from '@angular/core';
import { AuthorizationService } from '../../services/authorization/authorization.service';
import { AuthorizationStateService } from '../../services/authorization/authorization-state.service';
import { UserSessionService } from '../../services/user-session.service';
import { PermissionDirectiveOptions } from '../../models/ui/permission-directive-options.model';
import { PermissionUtilities } from '../../utilities/permission.utilities';
import { ProfileOrganizationScopeService } from '../../services/authorization/profile-organization-scope.service';
import { IfHasChildPermissionDirective } from '../if-has-child-permission/if-has-child-permission.directive';

@Directive({
	selector: '[disableWithoutPermission]',
	standalone: true
})
export class DisableWithoutPermissionDirective {
	private readonly elementRef = inject(ElementRef);
	private readonly renderer = inject(Renderer2);
	private clickBlocker = (e: Event) => { e.preventDefault(); e.stopPropagation(); };
	private disabledByDirective = false;
	private readonly authorizationService = inject(AuthorizationService);
	private readonly authorizationStateService = inject(AuthorizationStateService);
	private readonly userSessionService = inject(UserSessionService);
	private readonly parent = inject(IfHasChildPermissionDirective, { optional: true, skipSelf: true });
	// Provided by an org-scoped profile page (organization/member/property) so permissions resolve
	// against the organization on screen rather than the active one; null everywhere else.
	private readonly profileScope = inject(ProfileOrganizationScopeService, { optional: true });

	public readonly disableWithoutPermission = input.required<PermissionDirectiveOptions>();

	// Forces the element disabled regardless of permission (e.g. the entity is deleted).
	public readonly forceDisabled = input<boolean>(false);

	private readonly hasPermissionOrIsOwner = computed(() => {
		// Left enabled while the authorization state is still loading — deliberately the opposite of
		// ifHasPermission's fail-closed render decision, because a briefly-enabled control is
		// recoverable whereas a briefly-visible one has already leaked what it was hiding.
		if (!this.authorizationStateService.authorizationState()) {
			return true;
		}

		// Shares shouldOptionsRender with ifHasPermission rather than keeping a second copy of the
		// same logic. The copy this replaces read only the active organization, so
		// options.organizationId — declared on the PermissionDirectiveOptions both directives
		// take — type-checked here and silently did nothing (DEV-2643).
		return PermissionUtilities.shouldOptionsRender(
			this.disableWithoutPermission(),
			this.authorizationService,
			this.authorizationStateService,
			this.userSessionService,
			this.profileScope
		);
	});

	constructor() {
		const destroyRef = inject(DestroyRef);

		effect(() => {
			const disabled = !this.hasPermissionOrIsOwner() || this.forceDisabled();

			if (disabled) {
				this.disableElement();
			} else {
				this.enableElement();
			}
		});

		if (this.parent) {
			const parent = this.parent;
			const evaluator = () => true;
			parent.register(evaluator);
			destroyRef.onDestroy(() => parent.unregister(evaluator));
		}
	}

	private disableElement(): void {
		const el = this.elementRef.nativeElement;
		this.disabledByDirective = true;
		this.renderer.addClass(el, 'disabled');
		this.renderer.setAttribute(el, 'aria-disabled', 'true');
		this.renderer.setAttribute(el, 'tabindex', '-1');
		this.renderer.setStyle(el, 'pointer-events', 'none');
		el.addEventListener('click', this.clickBlocker, true);

		if (this.supportsNativeDisabled(el)) {
			this.renderer.setAttribute(el, 'disabled', 'true');
		}
	}

	private enableElement(): void {
		if (!this.disabledByDirective) return;

		const el = this.elementRef.nativeElement;
		this.disabledByDirective = false;
		this.renderer.removeClass(el, 'disabled');
		this.renderer.removeAttribute(el, 'aria-disabled');
		this.renderer.removeAttribute(el, 'tabindex');
		this.renderer.removeStyle(el, 'pointer-events');
		el.removeEventListener('click', this.clickBlocker, true);

		if (this.supportsNativeDisabled(el)) {
			this.renderer.removeAttribute(el, 'disabled');
		}
	}

	private supportsNativeDisabled(el: HTMLElement): boolean {
		const tag = el.tagName?.toUpperCase();
		return tag === 'BUTTON' || tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA';
	}

}
